// STARCODE particle engine core: owns the GPGPU simulation (position + velocity textures
// updated entirely on the GPU) and the Points render material that visualizes it. This is the
// piece that gives "geilere Performance" versus the CPU-bound Processing sketch - tens of
// thousands of particles are simulated in a handful of full-screen shader passes instead of a
// per-particle JavaScript/Java loop.

import * as THREE from "three";
import { GPUComputationRenderer } from "three/examples/jsm/misc/GPUComputationRenderer.js";

import noiseGLSL from "./gpgpu/noise.glsl?raw";
import velocityShaderGLSL from "./gpgpu/velocityShader.glsl?raw";
import positionShaderGLSL from "./gpgpu/positionShader.glsl?raw";
import pointsVertexGLSL from "./gpgpu/pointsVertex.glsl?raw";
import pointsFragmentGLSL from "./gpgpu/pointsFragment.glsl?raw";

import { buildTextTargets } from "./textTarget.js";
import { buildDefault3DMeshTargets, parseObjTargets } from "./objTarget.js";
import { state } from "./state.js";

// Second text morph target used as an on-screen credit/branding during a live set.
export const BRAND_WORD = "STUDIOPESC.COM";

// Particle presets: switchable on the fly without page reload
export const PARTICLE_PRESETS = [
  { count: 16384, size: 128, label: "16.384 (Eco)" },
  { count: 36864, size: 192, label: "36.864 (Low)" },
  { count: 65536, size: 256, label: "65.536 (Standard)" },
  { count: 147456, size: 384, label: "147.456 (High)" },
  { count: 262144, size: 512, label: "262.144 (Ultra 4K)" },
];

export const MAX_TEX_SIZE = 512;
export const MAX_PARTICLE_COUNT = MAX_TEX_SIZE * MAX_TEX_SIZE;

export function getInitialParticleCount() {
  if (typeof window === "undefined") return 65536;
  const urlParam = new URLSearchParams(window.location.search).get("particles");
  const stored = typeof localStorage !== "undefined" ? localStorage.getItem("starcode_particle_count") : null;
  const count = parseInt(urlParam || stored, 10);
  const preset = PARTICLE_PRESETS.find((p) => p.count === count);
  return preset ? preset.count : 65536;
}

export const PARTICLE_COUNT = getInitialParticleCount();
export const TEX_SIZE = MAX_TEX_SIZE;

export const BOUNDS_RADIUS = 420;

// Maps a themed effect's state flag name to a target HSL palette. Rather than hardcoding dozens
// of per-effect color branches into the fragment shader, every "thematic" ported effect (nebula,
// ember wind, ink bleed, galaxy family, liquid family, ...) just gets an entry here and the
// fragment shader blends toward it generically via uThemeHue/Sat/Light/Blend.
const THEME_COLORS = {
  nebulaOn: { h: 0.72, s: 0.5, l: 0.62 },
  emberOn: { h: 0.06, s: 0.85, l: 0.55 },
  swarmOn: { h: 0.33, s: 0.5, l: 0.55 },
  vortexOn: { h: 0.66, s: 0.6, l: 0.5 },
  galaxySpiralOn: { h: 0.7, s: 0.55, l: 0.55 },
  blackHoleOn: { h: 0.08, s: 0.95, l: 0.58 }, // blazing amber/orange relativistic accretion disk
  neutronStarOn: { h: 0.58, s: 0.92, l: 0.65 }, // electric pulsar cyan-blue & violet plasma
  sunOn: { h: 0.09, s: 0.98, l: 0.66 }, // radiant solar white-gold & amber corona
  fireballOn: { h: 0.09, s: 0.98, l: 0.66 },
  cyberGridOn: { h: 0.82, s: 0.88, l: 0.62 }, // neon electric magenta/cyan synthwave grid
  liquidRippleOn: { h: 0.5, s: 0.6, l: 0.55 },
  inkBleedOn: { h: 0.55, s: 0.75, l: 0.28 },
  portalOn: { h: 0.78, s: 0.65, l: 0.5 },
  causticsOn: { h: 0.47, s: 0.5, l: 0.65 },
  redCircleOn: { h: 0.0, s: 0.85, l: 0.5 },
  galaxyDiscOn: { h: 0.58, s: 0.45, l: 0.55 },
  sphereOn: { h: 0.52, s: 0.65, l: 0.62 }, // luminous cyan-aquamarine planetary sphere
  // Cool, desaturated cyan-white - reads as "rigid/technical" rather than organic, matching the
  // stiff square grid formation itself.
  gridPulseOn: { h: 0.55, s: 0.3, l: 0.65 },
};
const THEME_KEYS = Object.keys(THEME_COLORS);

function lerpHue(a, b, t) {
  let diff = ((b - a + 0.5) % 1) - 0.5;
  if (diff < -0.5) diff += 1;
  return ((a + diff * t) % 1 + 1) % 1;
}

export class ParticleSystem {
  constructor(renderer) {
    this.renderer = renderer;
    this.clockTime = 0;

    this.gpuCompute = new GPUComputationRenderer(MAX_TEX_SIZE, MAX_TEX_SIZE, renderer);

    const dtPosition = this.gpuCompute.createTexture();
    const dtVelocity = this.gpuCompute.createTexture();
    this._fillInitialTextures(dtPosition, dtVelocity);

    this.velocityVariable = this.gpuCompute.addVariable(
      "textureVelocity",
      noiseGLSL + "\n" + velocityShaderGLSL,
      dtVelocity
    );
    this.positionVariable = this.gpuCompute.addVariable(
      "texturePosition",
      positionShaderGLSL,
      dtPosition
    );

    this.gpuCompute.setVariableDependencies(this.velocityVariable, [this.positionVariable, this.velocityVariable]);
    this.gpuCompute.setVariableDependencies(this.positionVariable, [this.positionVariable, this.velocityVariable]);

    this.morphTargetTexture = new THREE.DataTexture(
      new Float32Array(MAX_PARTICLE_COUNT * 4),
      MAX_TEX_SIZE,
      MAX_TEX_SIZE,
      THREE.RGBAFormat,
      THREE.FloatType
    );
    // Nearest filtering: each texel is one particle's exact designated target point - linear
    // interpolation would blend unrelated points on the letters together at the sample edges.
    this.morphTargetTexture.minFilter = THREE.NearestFilter;
    this.morphTargetTexture.magFilter = THREE.NearestFilter;
    this.morphTargetTexture.generateMipmaps = false;
    this.morphTargetTexture.needsUpdate = true;

    const vUniforms = this.velocityVariable.material.uniforms;
    Object.assign(vUniforms, {
      uTime: { value: 0 },
      uDelta: { value: 0 },
      uAudioLow: { value: 0 },
      uAudioMid: { value: 0 },
      uAudioHigh: { value: 0 },
      uFlowStrength: { value: 6.5 },
      uBoundsRadius: { value: BOUNDS_RADIUS },
      uGravityOn: { value: 0 },
      uGravityStrength: { value: 70 },
      uSunOn: { value: 0 },
      uSunScale: { value: 0 },
      uSunStrength: { value: 65 },
      uFireballOn: { value: 0 },
      uFireballIntensity: { value: 55 },
      uFireballTurbulence: { value: 40 },
      uFireballCenter: { value: new THREE.Vector3(0, 0, 0) },
      uFireballCoreRadius: { value: BOUNDS_RADIUS * 0.16 },
      uFireballInfluenceRadius: { value: BOUNDS_RADIUS * 0.42 },
      uFireballOuterMultiplier: { value: 2.2 },
      uRingOn: { value: 0 },
      uRingIntensity: { value: 45 },
      uRingRadius: { value: BOUNDS_RADIUS * 0.55 },
      uRingThickness: { value: BOUNDS_RADIUS * 0.14 },
      uRippleOn: { value: 0 },
      uRippleRadius: { value: 0 },
      uRippleWidth: { value: 70 },
      uRippleStrength: { value: 60 },
      uExplodeTime: { value: -999 },
      uExplodeStrength: { value: 0 },
      uRbBurstTime: { value: -999 },
      uRbBurstStrength: { value: 0 },
      uShockwaveTime: { value: -999 },
      uShockwaveStrength: { value: 0 },

      // --- Audio-Reactivity switches ---
      uAudioReactGlobal: { value: 1.0 },
      uAudioReactAttractors: { value: 1.0 },
      uAudioReactBasis: { value: 1.0 },
      uAudioReactEffects: { value: 1.0 },
      uAudioReactExtended: { value: 1.0 },

      // --- Restored NEU.pde controls: held A/S attractors + numeric-key audio-reactive effects ---
      uAttractorNegOn: { value: 0 },
      uAttractorPosOn: { value: 0 },
      uAttractorStrength: { value: 30 },
      uSpiralHighOn: { value: 0 },
      uBandExplodeOn: { value: 0 },
      uShimmerOn: { value: 0 },
      uComplexOn: { value: 0 },

      // --- Extended effect set ---
      uNebulaOn: { value: 0 },
      uNebulaStrength: { value: 45 },
      uWellsOn: { value: 0 },
      uWellsStrength: { value: 55 },
      uWellsRadius: { value: BOUNDS_RADIUS * 0.5 },
      uEmberOn: { value: 0 },
      uEmberStrength: { value: 70 },
      uGalaxyDiscOn: { value: 0 },
      uGalaxyDiscStrength: { value: 60 },
      uSwarmOn: { value: 0 },
      uSwarmStrength: { value: 50 },
      uVortexOn: { value: 0 },
      uVortexStrength: { value: 65 },
      uGalaxySpiralOn: { value: 0 },
      uGalaxySpiralStrength: { value: 60 },
      uBlackHoleOn: { value: 0 },
      uBlackHoleStrength: { value: 65 },
      uNeutronStarOn: { value: 0 },
      uNeutronStarStrength: { value: 65 },
      uLiquidRippleOn: { value: 0 },
      uLiquidRippleStrength: { value: 55 },
      uInkBleedOn: { value: 0 },
      uInkBleedStrength: { value: 50 },
      uPortalOn: { value: 0 },
      uPortalStrength: { value: 60 },
      uCausticsOn: { value: 0 },
      uCausticsStrength: { value: 40 },
      uChaosBoost: { value: 1.0 },
      uDistortionOn: { value: 0 },
      uDistortionStrength: { value: 45 },
      uFractalOn: { value: 0 },
      uFractalStrength: { value: 50 },
      uPatternOn: { value: 0 },
      uPatternStrength: { value: 60 },
      uRedCircleOn: { value: 0 },
      uRedCircleStrength: { value: 60 },
      uShapeChangeOn: { value: 0 },
      uShapeChangeStrength: { value: 60 },
      uStreamsOn: { value: 0 },
      uGridPulseOn: { value: 0 },
      uGridPulseStrength: { value: 65 },
      uBlackHoleOn: { value: 0 },
      uBlackHoleStrength: { value: 65 },
      uNeutronStarOn: { value: 0 },
      uNeutronStarStrength: { value: 65 },
      uCyberGridOn: { value: 0 },
      uCyberGridStrength: { value: 65 },
      uSphereOn: { value: 0 },
      uSphereStrength: { value: 65 },

      uMorphOn: { value: 0 },
      uMorphElapsed: { value: 0 },
      uMorphStagger: { value: 0.9 },
      uMorphRamp: { value: 1.1 },
      uMorphTargetTexture: { value: this.morphTargetTexture },
      uMaxSpeed: { value: 150 },
      uSlowMotion: { value: 1 },

      // --- Global live-performance parameters + new audio-reactive motion effects ---
      uMasterIntensity: { value: 1.0 },
      uWrapOn: { value: 0 },
      uBeatPulseOn: { value: 0 },
      uBeatPulse: { value: 0 },
      uBeatTime: { value: -999 },
      uBreathOn: { value: 0 },
      uTunnelOn: { value: 0 },
      uHelixOn: { value: 0 },
      uStrobeFieldOn: { value: 0 },
      uAudioFlux: { value: 0 },
    });

    const pUniforms = this.positionVariable.material.uniforms;
    Object.assign(pUniforms, {
      uDelta: { value: 0 },
      uSlowMotion: { value: 1 },
      uWrapOn: { value: 0 },
      // Half-extent of the wrap box. Slightly larger than BOUNDS_RADIUS so the seam sits just
      // outside the usual swarm volume and particles wrap at the edge of frame, not mid-cloud.
      uWrapSize: { value: BOUNDS_RADIUS * 1.15 },
    });

    this.velocityVariable.wrapS = this.velocityVariable.wrapT = THREE.RepeatWrapping;
    this.positionVariable.wrapS = this.positionVariable.wrapT = THREE.RepeatWrapping;

    const error = this.gpuCompute.init();
    if (error !== null) {
      console.error("[STARCODE] GPUComputationRenderer init error:", error);
    }

    this._buildPoints();

    this._ripplePhaseTime = -999;
    this._morphReady = false;
    this._starcodeTargets = null;
    this._brandTargets = null;
    this._objTargets = null;
    this._customObjTargets = null;
    this._currentMorphType = "none";
    this._themeHue = 0.56;
    this._themeBlend = 0;
    this._sunScale = 0.0;
    this._lastBeatTime = -999;
    this._shockwaveTime = -999;
    this._shockwaveStrength = 0;

    // Startup intro: brief, deliberately unfinished STARCODE that dissolves back into the swarm.
    this._introT0 = null;
    this._introDone = true;
    this._introReleased = false;
    this._prevMorphing = false;
    this._morphSwitchTime = -999;
  }

  // Scripted startup sequence. Returns { on, elapsed } to drive the morph uniforms, or null when
  // the intro is inactive (then the regular user-controlled morph state applies).
  _updateIntro(userMorphing) {
    if (this._introDone || this._introT0 === null) return null;
    // A manual morph always wins - cancel the intro immediately.
    if (userMorphing) {
      this._introDone = true;
      return null;
    }

    const INTRO_DELAY = 0.35;   // pure particle cloud first
    const INTRO_PULL = 1.9;     // how long the lettering is pulled together
    const INTRO_CAP = 1.9;      // released before the per-particle stagger completes -> ~4/5 formed

    const t = this.clockTime - this._introT0;
    if (t < INTRO_DELAY) return { on: false, elapsed: 0 };

    const pull = t - INTRO_DELAY;
    if (pull < INTRO_PULL) {
      return { on: true, elapsed: Math.min(pull, INTRO_CAP) };
    }

    // Intro complete: trigger burst scatter into the swarm and finish intro permanently
    if (!this._introReleased) {
      this._introReleased = true;
      this.triggerExplosion(46);
    }
    this._introDone = true;
    return null;
  }

  _fillInitialTextures(dtPosition, dtVelocity) {
    const posArr = dtPosition.image.data;
    const velArr = dtVelocity.image.data;
    for (let i = 0; i < MAX_PARTICLE_COUNT; i++) {
      const i4 = i * 4;
      // Scatter inside a soft sphere so the idle swarm reads as a volumetric cloud from frame one.
      const r = BOUNDS_RADIUS * 0.55 * Math.cbrt(Math.random());
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      posArr[i4 + 0] = r * Math.sin(phi) * Math.cos(theta);
      posArr[i4 + 1] = r * Math.sin(phi) * Math.sin(theta);
      posArr[i4 + 2] = r * Math.cos(phi);
      posArr[i4 + 3] = Math.random(); // per-particle seed, used everywhere for jitter/hue/stagger

      velArr[i4 + 0] = (Math.random() - 0.5) * 4;
      velArr[i4 + 1] = (Math.random() - 0.5) * 4;
      velArr[i4 + 2] = (Math.random() - 0.5) * 4;
      velArr[i4 + 3] = 0;
    }
  }

  _buildPoints() {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(MAX_PARTICLE_COUNT * 3);
    const reference = new Float32Array(MAX_PARTICLE_COUNT * 2);

    for (let idx = 0; idx < MAX_PARTICLE_COUNT; idx++) {
      const i = idx % MAX_TEX_SIZE;
      const j = Math.floor(idx / MAX_TEX_SIZE);
      reference[idx * 2 + 0] = (i + 0.5) / MAX_TEX_SIZE;
      reference[idx * 2 + 1] = (j + 0.5) / MAX_TEX_SIZE;
    }

    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("reference", new THREE.BufferAttribute(reference, 2));
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), BOUNDS_RADIUS * 3);
    this.activeParticleCount = PARTICLE_COUNT;
    geometry.setDrawRange(0, this.activeParticleCount);

    this.material = new THREE.ShaderMaterial({
      uniforms: {
        texturePosition: { value: null },
        textureVelocity: { value: null },
        uPixelRatio: { value: Math.min(window.devicePixelRatio || 1, 2) },
        uBaseSize: { value: 3.6 },
        uSizeMultiplier: { value: 1.0 },
        uMorphOn: { value: 0 },
        uMorphElapsed: { value: 0 },
        uMorphStagger: { value: 0.9 },
        uMorphRamp: { value: 1.1 },
        uAudioHigh: { value: 0 },
        uAudioLow: { value: 0 },
        uAudioMid: { value: 0 },
        uTime: { value: 0 },
        uHueBase: { value: 0.56 },
        uOpacity: { value: 0.95 },
        uScalePulseOn: { value: 0 },
        uThemeHue: { value: 0.56 },
        uThemeSat: { value: 0.55 },
        uThemeLight: { value: 0.55 },
        uThemeBlend: { value: 0 },
        uColorInvert: { value: 0 },
        uPermanentColor: { value: 0 },
        uColorMidOn: { value: 0 },
        uStarSkyOn: { value: 0 },
        uShapeChangeOn: { value: 0 },
        uShimmerOn: { value: 0 },

        // --- New audio-reactive colour/size effects + global live parameters ---
        uBioluminescenceOn: { value: 0 },
        uBioIntensity: { value: 0.85 },
        uBioSpeed: { value: 0.55 },
        uSpectrumColorOn: { value: 0 },
        uChromaWaveOn: { value: 0 },
        uBeatFlashOn: { value: 0 },
        uAudioSizeOn: { value: 0 },
        uBeatPulse: { value: 0 },
        uAudioFlux: { value: 0 },
        uHueShift: { value: 0 },
        uSizeBoost: { value: 1 },
        uBoundsRadius: { value: BOUNDS_RADIUS },
        uAudioSub: { value: 0 },
        uAudioBass: { value: 0 },
        uAudioLowMid: { value: 0 },
        uAudioHighMid: { value: 0 },
        uAudioTreble: { value: 0 },
        uFocusDistance: { value: 780 },
        uDofBlur: { value: 0.45 },
        uDofInvert: { value: 0 },
        uDofAudioReact: { value: 0 },
        uSunScale: { value: 0 },
        uShockwaveTime: { value: -999 },
        uShockwaveStrength: { value: 0 },
      },
      vertexShader: pointsVertexGLSL,
      fragmentShader: pointsFragmentGLSL,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    this.points = new THREE.Points(geometry, this.material);
    this.points.frustumCulled = false;
  }

  setParticleCount(newCount) {
    const count = Math.max(1024, Math.min(newCount, MAX_PARTICLE_COUNT));
    this.activeParticleCount = count;
    if (this.points?.geometry) {
      this.points.geometry.setDrawRange(0, count);
    }
    const particleCountEl = document.getElementById("particleCount");
    if (particleCountEl) {
      particleCountEl.textContent = `${count.toLocaleString("de-DE")} Partikel`;
    }
  }

  resetSwarm() {
    // Re-seeds the GPGPU position & velocity textures into a fresh, clean volumetric cloud
    this._introDone = true;
    this._introT0 = null;
    const initPos = this.gpuCompute.createTexture();
    const posArr = initPos.image.data;
    const initVel = this.gpuCompute.createTexture();
    const velArr = initVel.image.data;

    for (let i = 0; i < MAX_PARTICLE_COUNT; i++) {
      const i4 = i * 4;
      const r = BOUNDS_RADIUS * 0.55 * Math.cbrt(Math.random());
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      posArr[i4 + 0] = r * Math.sin(phi) * Math.cos(theta);
      posArr[i4 + 1] = r * Math.sin(phi) * Math.sin(theta);
      posArr[i4 + 2] = r * Math.cos(phi);
      posArr[i4 + 3] = Math.random();

      velArr[i4 + 0] = (Math.random() - 0.5) * 4;
      velArr[i4 + 1] = (Math.random() - 0.5) * 4;
      velArr[i4 + 2] = (Math.random() - 0.5) * 4;
      velArr[i4 + 3] = 0;
    }

    this.gpuCompute.renderTexture(initPos, this.positionVariable.renderTargets[0]);
    this.gpuCompute.renderTexture(initPos, this.positionVariable.renderTargets[1]);
    this.gpuCompute.renderTexture(initVel, this.velocityVariable.renderTargets[0]);
    this.gpuCompute.renderTexture(initVel, this.velocityVariable.renderTargets[1]);
  }

  async loadMorphTarget(word) {
    this._starcodeTargets = await buildTextTargets(word, MAX_PARTICLE_COUNT);
    this._objTargets = buildDefault3DMeshTargets(MAX_PARTICLE_COUNT);
    this._customObjTargets = null;
    this.morphTargetTexture.image.data.set(this._starcodeTargets);
    this.morphTargetTexture.needsUpdate = true;
    this._morphReady = true;

    // Branding lettering is built in the background so the first click on the STUDIOPESC.COM
    // button is instant. It must not delay the startup intro, hence no await here. The word is
    // 14 characters against STARCODE's 8, so it gets a proportionally wider target box - that
    // keeps the individual glyphs the same size and therefore just as readable.
    buildTextTargets(BRAND_WORD, MAX_PARTICLE_COUNT, { targetWidth: 1100 })
      .then((targets) => { this._brandTargets = targets; })
      .catch((err) => console.error("[STARCODE] Branding-Morph konnte nicht gebaut werden:", err));

    // Start from a plain particle cloud, then let the scripted intro (see _updateIntro) briefly
    // pull the lettering together and release it again into the swarm (only in main UI, not output window).
    this.resetSwarm();
    const isDedicatedOutput = typeof window !== "undefined" && (new URLSearchParams(window.location.search).get("role") === "output" || new URLSearchParams(window.location.search).get("live") === "1");
    if (!isDedicatedOutput) {
      this._introT0 = this.clockTime;
      this._introDone = false;
      this._introReleased = false;
    } else {
      this._introDone = true;
      this._introT0 = null;
    }
  }

  loadCustomObj(objText, name = "3D-Modell") {
    try {
      const targets = parseObjTargets(objText, MAX_PARTICLE_COUNT);
      this._customObjTargets = targets;
      state.loadedObjName = name;
      if (state.objMorphOn) {
        this.morphTargetTexture.image.data.set(targets);
        this.morphTargetTexture.needsUpdate = true;
      }
      return { success: true, name };
    } catch (err) {
      console.error("[STARCODE] OBJ Parse Error:", err);
      return { success: false, error: err.message };
    }
  }

  triggerExplosion(strength = 80) {
    const u = this.velocityVariable.material.uniforms;
    u.uExplodeTime.value = this.clockTime;
    u.uExplodeStrength.value = strength;
  }

  triggerRbBurst(strength = 90) {
    const u = this.velocityVariable.material.uniforms;
    u.uRbBurstTime.value = this.clockTime;
    u.uRbBurstStrength.value = strength;
  }

  triggerShockwave(strength = 180) {
    this._shockwaveTime = this.clockTime;
    this._shockwaveStrength = strength;
    const u = this.velocityVariable.material.uniforms;
    u.uShockwaveTime.value = this.clockTime;
    u.uShockwaveStrength.value = strength;
  }

  setSizeMultiplier(v) {
    this.material.uniforms.uSizeMultiplier.value = v;
  }

  setSunScale(scale) {
    this._sunScale = scale;
  }

  update(dt, audio) {
    this.clockTime += dt;
    const vUniforms = this.velocityVariable.material.uniforms;
    const pUniforms = this.positionVariable.material.uniforms;

    // Global audio gain (MIDI-controllable) applied once here, so every downstream effect - GPU
    // forces, colours, sizes - reacts to the same boosted/attenuated signal.
    const gain = state.audioGain;
    const aLow = Math.min(1.5, audio.low * gain);
    const aMid = Math.min(1.5, audio.mid * gain);
    const aHigh = Math.min(1.5, audio.high * gain);

    // Latch the timestamp of the most recent beat so the GPU shockwave can measure its own age.
    if (audio.beat || audio.beatPulse > 0.85) this._lastBeatTime = this.clockTime;

    // Combined low/mid/high energy (0..1-ish) - the GPU-port equivalent of NEU.pde's
    // rawAudioSignal(lowE, midE, highE), used by several of the restored numeric-key effects.
    const combinedSignal = (aLow + aMid + aHigh) / 3;

    // Slow Motion (Taste 1) - matches applySlowMotion()'s continuous formula: even at silence the
    // audio "amount" has a floor of 0.35 (some slow-down always applied while active), and it
    // deepens continuously with real audio energy - NOT the old hard binary 0.18 switch.
    let slow = 1.0;
    if (state.slowMotion) {
      const amount = Math.min(1, Math.max(0.35, combinedSignal));
      const factor = 1.0 - 0.85 * amount; // lerp(1.0, ~0.15, amount)
      slow = Math.min(1.0, Math.max(0.02, factor));
    }
    // Global speed scale (MIDI-controllable) rides on top of slow motion.
    slow *= state.speedScale;

    vUniforms.uTime.value = this.clockTime;
    vUniforms.uDelta.value = dt;
    vUniforms.uSlowMotion.value = slow;
    vUniforms.uAudioLow.value = aLow;
    vUniforms.uAudioMid.value = aMid;
    vUniforms.uAudioHigh.value = aHigh;
    vUniforms.uAudioFlux.value = audio.flux;
    vUniforms.uBeatPulse.value = audio.beatPulse;
    vUniforms.uBeatTime.value = this._lastBeatTime ?? -999;
    vUniforms.uMasterIntensity.value = state.masterIntensity;

    vUniforms.uBeatPulseOn.value = state.beatPulseOn ? 1 : 0;
    vUniforms.uBreathOn.value = state.breathOn ? 1 : 0;
    vUniforms.uTunnelOn.value = state.tunnelOn ? 1 : 0;
    vUniforms.uHelixOn.value = state.helixOn ? 1 : 0;
    vUniforms.uStrobeFieldOn.value = state.strobeFieldOn ? 1 : 0;

    // Audio-reactivity modulation switches per section + master.
    vUniforms.uAudioReactGlobal.value = state.audioReactGlobal ? 1 : 0;
    vUniforms.uAudioReactAttractors.value = state.audioReactAttractors ? 1 : 0;
    vUniforms.uAudioReactBasis.value = state.audioReactBasis ? 1 : 0;
    vUniforms.uAudioReactEffects.value = state.audioReactEffects ? 1 : 0;
    vUniforms.uAudioReactExtended.value = state.audioReactExtended ? 1 : 0;

    // Toroidal wrap mode - the velocity shader skips its inward bounds force while this is on.
    vUniforms.uWrapOn.value = state.wrapModeOn ? 1 : 0;
    pUniforms.uWrapOn.value = state.wrapModeOn ? 1 : 0;

    // Held A/S attractors - direct booleans set by keydown/keyup (see keybinds.js), not toggles.
    vUniforms.uAttractorNegOn.value = state.negAttractorHeld ? 1 : 0;
    vUniforms.uAttractorPosOn.value = state.posAttractorHeld ? 1 : 0;

    // Restored numeric-key audio-reactive effects (4, 5, 7, ß).
    vUniforms.uSpiralHighOn.value = state.spiralHighOn ? 1 : 0;
    vUniforms.uBandExplodeOn.value = state.bandExplodeOn ? 1 : 0;
    vUniforms.uShimmerOn.value = state.shimmerOn ? 1 : 0;
    vUniforms.uComplexOn.value = state.complexOn ? 1 : 0;

    vUniforms.uGravityOn.value = state.gravityOn ? 1 : 0;
    const isSunActive = state.sunOn || state.fireballOn;
    const sunTarget = isSunActive ? 1.0 : 0.0;
    const sunSmoothing = Math.min(1, dt * (isSunActive ? 1.8 : 2.5));
    this._sunScale += (sunTarget - this._sunScale) * sunSmoothing;
    vUniforms.uSunOn.value = isSunActive ? 1 : 0;
    vUniforms.uSunScale.value = this._sunScale;
    this.material.uniforms.uSunScale.value = this._sunScale;
    vUniforms.uFireballOn.value = state.fireballOn ? 1 : 0;
    vUniforms.uRingOn.value = state.ringOn ? 1 : 0;
    vUniforms.uRippleOn.value = state.rippleOn ? 1 : 0;

    // --- Extended effect set: push on/off flags straight from state every frame. ---
    vUniforms.uNebulaOn.value = state.nebulaOn ? 1 : 0;
    vUniforms.uWellsOn.value = state.wellsOn ? 1 : 0;
    vUniforms.uEmberOn.value = state.emberOn ? 1 : 0;
    vUniforms.uGalaxyDiscOn.value = state.galaxyDiscOn ? 1 : 0;
    vUniforms.uSwarmOn.value = state.swarmOn ? 1 : 0;
    vUniforms.uVortexOn.value = state.vortexOn ? 1 : 0;
    vUniforms.uGalaxySpiralOn.value = state.galaxySpiralOn ? 1 : 0;
    vUniforms.uBlackHoleOn.value = state.blackHoleOn ? 1 : 0;
    vUniforms.uNeutronStarOn.value = state.neutronStarOn ? 1 : 0;
    vUniforms.uLiquidRippleOn.value = state.liquidRippleOn ? 1 : 0;
    vUniforms.uInkBleedOn.value = state.inkBleedOn ? 1 : 0;
    vUniforms.uPortalOn.value = state.portalOn ? 1 : 0;
    vUniforms.uCausticsOn.value = state.causticsOn ? 1 : 0;
    vUniforms.uChaosBoost.value = state.chaosBoostOn ? 2.6 : 1.0;
    vUniforms.uDistortionOn.value = state.distortionOn ? 1 : 0;
    vUniforms.uFractalOn.value = state.fractalOn ? 1 : 0;
    vUniforms.uPatternOn.value = state.patternOn ? 1 : 0;
    vUniforms.uRedCircleOn.value = state.redCircleOn ? 1 : 0;
    vUniforms.uShapeChangeOn.value = state.shapeChangeOn ? 1 : 0;
    vUniforms.uStreamsOn.value = state.streamsOn ? 1 : 0;
    vUniforms.uGridPulseOn.value = state.gridPulseOn ? 1 : 0;
    vUniforms.uCyberGridOn.value = state.cyberGridOn ? 1 : 0;
    vUniforms.uSphereOn.value = state.sphereOn ? 1 : 0;

    // Themed color blend: find the currently active themed effect (if any) and smoothly blend
    // the render palette toward it; otherwise ease back to the default flow palette.
    let targetThemeKey = null;
    if (state.lastToggledEffect && state[state.lastToggledEffect] && THEME_COLORS[state.lastToggledEffect]) {
      targetThemeKey = state.lastToggledEffect;
    } else {
      targetThemeKey = THEME_KEYS.find((k) => state[k]) || null;
    }
    const themeSmoothing = Math.min(1, dt * 3.5);
    if (targetThemeKey) {
      const theme = THEME_COLORS[targetThemeKey];
      this._themeHue = lerpHue(this._themeHue, theme.h, themeSmoothing);
      this._themeBlend += (1 - this._themeBlend) * themeSmoothing;
      this._themeSat = (this._themeSat ?? theme.s) + (theme.s - (this._themeSat ?? theme.s)) * themeSmoothing;
      this._themeLight = (this._themeLight ?? theme.l) + (theme.l - (this._themeLight ?? theme.l)) * themeSmoothing;
    } else {
      this._themeBlend += (0 - this._themeBlend) * themeSmoothing;
    }
    this.material.uniforms.uThemeHue.value = this._themeHue;
    this.material.uniforms.uThemeSat.value = this._themeSat ?? 0.55;
    this.material.uniforms.uThemeLight.value = this._themeLight ?? 0.55;
    this.material.uniforms.uThemeBlend.value = this._themeBlend;

    this.material.uniforms.uScalePulseOn.value = state.scalePulseOn ? 1 : 0;
    this.material.uniforms.uColorInvert.value = state.colorInvertOn ? 1 : 0;
    this.material.uniforms.uPermanentColor.value = state.permanentColor;
    this.material.uniforms.uColorMidOn.value = state.colorMidOn ? 1 : 0;
    this.material.uniforms.uStarSkyOn.value = state.starSkyOn ? 1 : 0;
    this.material.uniforms.uShapeChangeOn.value = state.shapeChangeOn ? 1 : 0;
    this.material.uniforms.uShimmerOn.value = state.shimmerOn ? 1 : 0;

    // New audio-reactive colour/size effects + global live parameters.
    this.material.uniforms.uBioluminescenceOn.value = state.bioluminescenceOn ? 1 : 0;
    this.material.uniforms.uBioIntensity.value = state.bioIntensity ?? 0.85;
    this.material.uniforms.uBioSpeed.value = state.bioSpeed ?? 0.55;
    this.material.uniforms.uSpectrumColorOn.value = state.spectrumColorOn ? 1 : 0;
    this.material.uniforms.uChromaWaveOn.value = state.chromaWaveOn ? 1 : 0;
    this.material.uniforms.uBeatFlashOn.value = state.beatFlashOn ? 1 : 0;
    this.material.uniforms.uAudioSizeOn.value = state.audioSizeOn ? 1 : 0;
    this.material.uniforms.uBeatPulse.value = audio.beatPulse;
    this.material.uniforms.uAudioFlux.value = audio.flux;
    this.material.uniforms.uHueShift.value = state.hueShift;
    this.material.uniforms.uSizeBoost.value = state.sizeBoost;

    // Bokeh / DOF Audio-Reactivity: shift focal plane dynamically in time with the beat rhythm
    let effectiveFocus = state.focusDistance ?? 780;
    if (state.dofAudioReact) {
      const beatFreq = audio.bpm && audio.bpm > 40 ? (audio.bpm / 60) * Math.PI : 3.2;
      const rhythmicOscillation = Math.sin(this.clockTime * beatFreq) * 240.0;
      const kickShift = (audio.beatPulse * 260.0 - audio.bass * 140.0) * gain;
      effectiveFocus = THREE.MathUtils.clamp(effectiveFocus + rhythmicOscillation + kickShift, 200, 1500);
    }
    this.material.uniforms.uFocusDistance.value = effectiveFocus;
    this.material.uniforms.uDofBlur.value = state.dofBlur ?? 0.45;
    this.material.uniforms.uDofInvert.value = state.dofInvert ? 1 : 0;
    this.material.uniforms.uDofAudioReact.value = state.dofAudioReact ? 1 : 0;
    this.material.uniforms.uShockwaveTime.value = this._shockwaveTime ?? -999;
    this.material.uniforms.uShockwaveStrength.value = this._shockwaveStrength ?? 0;
    this.material.uniforms.uAudioSub.value = Math.min(1.5, audio.sub * gain);
    this.material.uniforms.uAudioBass.value = Math.min(1.5, audio.bass * gain);
    this.material.uniforms.uAudioLowMid.value = Math.min(1.5, audio.lowMid * gain);
    this.material.uniforms.uAudioHighMid.value = Math.min(1.5, audio.highMid * gain);
    this.material.uniforms.uAudioTreble.value = Math.min(1.5, audio.treble * gain);

    // MIDI mod wheel (CC7) drives whichever effect was toggled most recently - same "auto target"
    // spirit as applyMidiControlToSelectedTarget()/autoMidiTargetId() in NEU.pde.
    const midi = state.midiValue;
    if (state.lastToggledEffect === "gravity") vUniforms.uGravityStrength.value = 20 + midi * 160;
    if (state.lastToggledEffect === "fireball") {
      vUniforms.uFireballIntensity.value = 10 + midi * 140;
      vUniforms.uFireballTurbulence.value = 5 + midi * 110;
    }
    if (state.lastToggledEffect === "ring") vUniforms.uRingIntensity.value = 8 + midi * 140;
    if (state.lastToggledEffect === "ripple") vUniforms.uRippleStrength.value = 10 + midi * 160;

    // Restored audio-reactivity for the number-row effects that used to be static in the port:
    // Pattern Formation (Taste 9) ring radius/pull now breathes with the real combined signal,
    // like applyPatternFormation()'s signal*patternFormationIntensity term.
    vUniforms.uPatternStrength.value = 30 + combinedSignal * 110;
    // Shape Change (Taste 0) base spring pull also gets a light combined-signal boost; the actual
    // per-group low/mid/high band split now happens per-particle in the vertex shader (point size).
    vUniforms.uShapeChangeStrength.value = 45 + combinedSignal * 60;

    if (state.rippleOn) {
      if (this._ripplePhaseTime < 0) this._ripplePhaseTime = this.clockTime;
      const elapsed = this.clockTime - this._ripplePhaseTime;
      const period = 1.4;
      const phase = elapsed % period;
      vUniforms.uRippleRadius.value = phase * (BOUNDS_RADIUS * 1.35 / period);
    } else {
      this._ripplePhaseTime = -999;
    }

    // Dynamic morph target texture selection (STARCODE text vs STUDIOPESC.COM branding vs 3D OBJ model)
    const activeMorph = state.morphOn ? "starcode" : state.brandMorphOn ? "brand" : state.objMorphOn ? "obj" : "none";
    if (activeMorph !== this._currentMorphType) {
      if (activeMorph === "starcode" && this._starcodeTargets) {
        this.morphTargetTexture.image.data.set(this._starcodeTargets);
        this.morphTargetTexture.needsUpdate = true;
      } else if (activeMorph === "brand" && this._brandTargets) {
        this.morphTargetTexture.image.data.set(this._brandTargets);
        this.morphTargetTexture.needsUpdate = true;
      } else if (activeMorph === "obj") {
        const objTarget = this._customObjTargets || this._objTargets || buildDefault3DMeshTargets(MAX_PARTICLE_COUNT);
        this.morphTargetTexture.image.data.set(objTarget);
        this.morphTargetTexture.needsUpdate = true;
      }
      this._currentMorphType = activeMorph;
    }

    let isMorphing = state.morphOn || state.objMorphOn || (state.brandMorphOn && !!this._brandTargets);

    // Track the morph on/off transition against the simulation clock. `state.morphToggleTime` uses
    // performance.now() which is a different time base than `clockTime`, and a "not morphing"
    // elapsed of 0 would make the shader's `1.0 - local` term evaluate to a full morph weight -
    // which is what used to freeze the lettering on screen permanently.
    if (isMorphing !== this._prevMorphing) {
      this._prevMorphing = isMorphing;
      this._morphSwitchTime = this.clockTime;
    }
    let elapsedMorph = Math.max(0, this.clockTime - this._morphSwitchTime);

    // Intro sequence overrides the morph uniforms while it runs (and only if the user has not
    // triggered a morph himself).
    const intro = this._updateIntro(isMorphing);
    if (intro) {
      isMorphing = intro.on;
      elapsedMorph = intro.elapsed;
    }

    vUniforms.uMorphOn.value = isMorphing ? 1 : 0;
    vUniforms.uMorphElapsed.value = elapsedMorph;

    this.gpuCompute.compute();

    const posTex = this.gpuCompute.getCurrentRenderTarget(this.positionVariable).texture;
    const velTex = this.gpuCompute.getCurrentRenderTarget(this.velocityVariable).texture;
    this.material.uniforms.texturePosition.value = posTex;
    this.material.uniforms.textureVelocity.value = velTex;
    this.material.uniforms.uMorphOn.value = isMorphing ? 1 : 0;
    this.material.uniforms.uMorphElapsed.value = elapsedMorph;
    this.material.uniforms.uAudioLow.value = aLow;
    this.material.uniforms.uAudioMid.value = aMid;
    this.material.uniforms.uAudioHigh.value = aHigh;
    this.material.uniforms.uTime.value = this.clockTime;
    this.material.uniforms.uSizeMultiplier.value = state.sizeMultiplier;

    pUniforms.uDelta.value = dt;
    pUniforms.uSlowMotion.value = slow;
  }
}
